import { createContext, useContext, type ReactNode } from "react";
import type {
  CreativeOptionKind,
  StoreCategoryId,
  StoreItemId,
  ToolId,
} from "../../game/store-catalog.js";

export type StudioStoreOpenRequest = {
  itemId?: StoreItemId;
  /** Tool category to open the store on (Clips / Focus / … / Sticker). */
  tool?: ToolId;
  /** @deprecated legacy category focus; tool is preferred. */
  category?: StoreCategoryId;
  /**
   * Preferred focus restore target — usually the creative option card button
   * (stable across locked→owned). Avoid the Get in Store button; it unmounts after buy.
   */
  returnFocus?: HTMLElement | null;
  /** Fallback selector if returnFocus disconnects (e.g. `[data-testid="look-card-…"]`). */
  returnFocusSelector?: string;
};

export type StudioStoreContextValue = {
  purchases: readonly StoreItemId[];
  cash: number;
  bypassOwnership: boolean;
  inChallenge: boolean;
  openStore: (req?: StudioStoreOpenRequest) => void;
  isOptionOwned: (kind: CreativeOptionKind, optionId: string) => boolean;
};

const StudioStoreContext = createContext<StudioStoreContextValue | null>(null);

export function StudioStoreProvider({
  value,
  children,
}: {
  value: StudioStoreContextValue;
  children: ReactNode;
}) {
  return (
    <StudioStoreContext.Provider value={value}>{children}</StudioStoreContext.Provider>
  );
}

export function useStudioStore(): StudioStoreContextValue {
  const ctx = useContext(StudioStoreContext);
  if (!ctx) {
    // Tools may render in isolation (tests) — treat everything as owned.
    return {
      purchases: [],
      cash: 0,
      bypassOwnership: true,
      inChallenge: false,
      openStore: () => undefined,
      isOptionOwned: () => true,
    };
  }
  return ctx;
}
